import { call, put, takeLatest, select } from 'redux-saga/effects';
import { current, PayloadAction } from '@reduxjs/toolkit';

import { getApiClient } from '@app/utils/apiUtils';
import {
  getParlingoGames,
  getParlingoGamesByStatus,
  getParlingoGamesSuccess,
  getParlingoGamesFailure,
  createParlingoGame,
  createParlingoGameSuccess,
  createParlingoGameFailure,
  updateParlingoGameStatus,
  updateParlingoGameStatusSuccess,
  updateParlingoGameStatusFailure,
  previewParlingoGame,
  previewParlingoGameSuccess,
  previewParlingoGameFailure,
  getAvailableFixtures,
  getAvailableFixturesSuccess,
  getAvailableFixturesFailure,
  getParlingoDifficultyOddsRangeSuccess,
  getParlingoDifficultyOddsRangeFailure,
  getParlingoDifficultyOddsRange,
  getAcceptedMarketDatalist,
  getAcceptedMarketDatalistSuccess,
  getAcceptedMarketDatalistFailure
} from './reducer';

// Use the same pattern as other containers
console.log('ParlingoSaga: Getting API client');

/**
 * Fetch paginated Parlingo games with optional status filter
 */
function* getParlingoGamesSaga() {
  try {
    console.log('ParlingoSaga: getParlingoGamesSaga called');

    // Get current state to determine pagination and filter params
    const state = yield select();
    const currentPage = state.parlingoContainer?.currentPage || 1;
    const pageSize = state.parlingoContainer?.pageSize || 10;
    const currentStatus = state.parlingoContainer?.currentStatus || 'all';
    const queryParams = new URLSearchParams();
    queryParams.append('pageNumber', currentPage);
    queryParams.append('limit', pageSize);

    // Only add status filter if not 'all'
    if (currentStatus !== 'all') {
      queryParams.append('status', currentStatus);
    }
    const queryString = queryParams.toString();

    console.log('ParlingoSaga: API params:', queryString);

    const response = yield call([getApiClient(), 'get'], `/parlingo-v2/games/available?${queryString}`);

    console.log('ParlingoSaga: API response:', response.data);

    // Handle both possible response structures
    const responseData = response.data.data;
    const games = responseData?.games || responseData || [];
    const paginationData = {
      games,
      totalCount: responseData?.totalCount || games.length,
      pageNumber: responseData?.pageNumber || currentPage,
      limit: responseData?.limit || pageSize,
      totalPages: responseData?.totalPages || Math.ceil((responseData?.totalCount || games.length) / pageSize)
    };

    console.log('ParlingoSaga: Processed pagination data:', paginationData);
    yield put(getParlingoGamesSuccess(paginationData));
  } catch (error: any) {
    console.error('ParlingoSaga: getParlingoGamesSaga error:', error);
    yield put(getParlingoGamesFailure(error?.response?.data?.message || 'Failed to fetch Parlingo games'));
  }
}

/**
 * Fetch Parlingo games by specific status
 */
function* getParlingoGamesByStatusSaga(action: PayloadAction<{ status: string; pageNumber?: number; limit?: number }>) {
  try {
    const { status, pageNumber = 1, limit = 50 } = action.payload;
    console.log('ParlingoSaga: getParlingoGamesByStatusSaga called', { status, pageNumber, limit });

    const response = yield call([getApiClient(), 'get'], '/parlingo-v2/games/available', {
      params: { status, pageNumber, limit }
    });
    console.log('ParlingoSaga: getParlingoGamesByStatusSaga response:', response.data);
    yield put(getParlingoGamesSuccess(response.data.data?.games || response.data.data || []));
  } catch (error: any) {
    console.error('ParlingoSaga: getParlingoGamesByStatusSaga error:', error);
    yield put(getParlingoGamesFailure(error?.response?.data?.message || 'Failed to fetch Parlingo games'));
  }
}

/**
 * Create new Parlingo game
 */
function* createParlingoGameSaga(action: PayloadAction<any>) {
  try {
    // Transform fixtureIds from comma-separated string to array
    const gameData = {
      ...action.payload,
      fixtureIds:
        typeof action.payload.fixtureIds === 'string'
          ? action.payload.fixtureIds
              .split(',')
              .map((id: string) => id.trim())
              .filter(Boolean)
          : action.payload.fixtureIds
    };

    const response = yield call([getApiClient(), 'post'], '/parlingo-v2/games', {
      input: gameData
    });

    yield put(createParlingoGameSuccess(response.data.data));

    // Refresh the games list
    yield put(getParlingoGames());
  } catch (error: any) {
    yield put(createParlingoGameFailure(error?.response?.data?.message || 'Failed to create Parlingo game'));
  }
}

/**
 * Update Parlingo game status - Use publish endpoint for draft->upcoming
 */
function* updateParlingoGameStatusSaga(action: PayloadAction<{ id: string; status: string }>) {
  try {
    const { id, status } = action.payload;

    // Use the publish endpoint for changing draft to upcoming
    if (status === 'upcoming') {
      const response = yield call([getApiClient(), 'put'], '/parlingo-v2/games/publish', null, {
        params: { gameId: id }
      });
      yield put(updateParlingoGameStatusSuccess({ _id: id, status: 'upcoming', ...response.data.data }));
    } else {
      // For other status changes, we might need a different endpoint
      // For now, throw an error as only publish (draft->upcoming) is implemented
      throw new Error(`Status change to '${status}' is not supported. Only draft->upcoming is available.`);
    }
  } catch (error: any) {
    yield put(
      updateParlingoGameStatusFailure(error?.response?.data?.message || error.message || 'Failed to update game status')
    );
  }
}

/**
 * Preview Parlingo game - DISABLED (not implemented in backend)
 */
function* previewParlingoGameSaga(action: PayloadAction<any>) {
  try {
    // Preview functionality is not implemented in parlingo-v2 backend
    // For now, just return the game data as preview
    const gameData = {
      ...action.payload,
      fixtureIds:
        typeof action.payload.fixtureIds === 'string'
          ? action.payload.fixtureIds
              .split(',')
              .map((id: string) => id.trim())
              .filter(Boolean)
          : action.payload.fixtureIds
    };

    yield put(previewParlingoGameSuccess(gameData));
  } catch (error: any) {
    yield put(previewParlingoGameFailure(error?.response?.data?.message || 'Preview functionality not available'));
  }
}

/**
 * Fetch available fixtures for parlingo games
 */
function* getAvailableFixturesSaga(action: PayloadAction<{ sport: string; leagues: string[] }>) {
  try {
    const { sport, leagues } = action.payload;
    console.log('ParlingoSaga: getAvailableFixturesSaga called', { sport, leagues });

    // Manually construct query string to ensure arrays are serialized correctly
    const queryParams = new URLSearchParams();
    queryParams.append('sport', sport);
    leagues.forEach((league) => {
      queryParams.append('leagues', league);
    });
    queryParams.append('startDateAfter', new Date().toISOString());
    queryParams.append('pageNumber', '1');

    const queryString = queryParams.toString();
    console.log('ParlingoSaga: Making API call to /parlingo-v2/fixtures', { queryString });

    const response = yield call([getApiClient(), 'get'], `/parlingo-v2/fixtures?${queryString}`);
    console.log('ParlingoSaga: API response received', response.data.data.fixtures);
    yield put(getAvailableFixturesSuccess(response.data.data.fixtures || []));
  } catch (error: any) {
    console.error('ParlingoSaga: Error fetching available fixtures', { error, response: error?.response });
    yield put(getAvailableFixturesFailure(error?.response?.data?.message || 'Failed to fetch available fixtures'));
  }
}

/**
 * Fetch difficulty odds range
 */
function* getParlingoDifficultyOddsRangeSaga() {
  try {
    const response = yield call([getApiClient(), 'get'], `/parlingo-v2/difficulty-odds-range`);

    // Handle both possible response structures
    const responseData = response.data.data;

    console.log('ParlingoSaga: getParlingoDifficultyOddsRangeSaga data:', responseData);
    yield put(getParlingoDifficultyOddsRangeSuccess(responseData));
  } catch (error: any) {
    console.error('ParlingoSaga: getParlingoDifficultyOddsRangeSaga error:', error);
    yield put(
      getParlingoDifficultyOddsRangeFailure(
        error?.response?.data?.message || 'Failed to fetch Difficulty Odds RangeSaga'
      )
    );
  }
}
/**
 * Fetch Accepted Market Data List
 */
function* getAcceptedMarketDatalistSaga() {
  try {
    const response = yield call([getApiClient(), 'get'], `/parlingo-v2/market-list-league`);

    // Handle both possible response structures
    const responseData = response.data.data;

    console.log('ParlingoSaga: ggetAcceptedMarketDatalistSaga data:', responseData);
    yield put(getAcceptedMarketDatalistSuccess(responseData));
  } catch (error: any) {
    console.error('ParlingoSaga: getAcceptedMarketDatalistSaga error:', error);
    yield put(
      getAcceptedMarketDatalistFailure(error?.response?.data?.message || 'Failed to fetch accepted market ListSaga')
    );
  }
}
/**
 * Root saga
 */
export default function* parlingoContainerSaga() {
  yield takeLatest(getParlingoGames.type, getParlingoGamesSaga);
  yield takeLatest(getParlingoGamesByStatus.type, getParlingoGamesByStatusSaga);
  yield takeLatest(createParlingoGame.type, createParlingoGameSaga);
  yield takeLatest(updateParlingoGameStatus.type, updateParlingoGameStatusSaga);
  yield takeLatest(previewParlingoGame.type, previewParlingoGameSaga);
  yield takeLatest(getAvailableFixtures.type, getAvailableFixturesSaga);
  yield takeLatest(getParlingoDifficultyOddsRange.type, getParlingoDifficultyOddsRangeSaga);
  yield takeLatest(getAcceptedMarketDatalist.type, getAcceptedMarketDatalistSaga);
}
